import {
  defaultShouldDehydrateQuery,
  type DehydrateOptions,
  type Query,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  persistQueryClientSave,
  type PersistedClient,
  type Persister,
} from '@tanstack/react-query-persist-client';
import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { ROLES, type Role } from '@servwell/shared';
import type { Session } from '../v2/session';
import type { Kv } from './browser';
import { accountRefKey } from './account';
import { pathOf } from './offline-replay';
import type { PendingWrite } from './pending-writes';
import { carriedFrom } from './legacy-writes';

/**
 * Which account the saved query cache belongs to (MAP 225a).
 *
 * The cache is persisted to IndexedDB for 24 hours so a tech in a basement still sees the
 * day. Before this file it belonged to nobody: on a shared tablet the next person to sign in
 * was handed the previous person's screens, and possibly another shop's, until each one
 * happened to refetch, or for as long as the device stayed offline.
 *
 * The rules, in one place:
 *
 *  1. **`me.session` is never saved** (`shouldDehydrateQuery`), and a blob saved before this
 *     release has it stripped on the way back in. So whatever `me.session` the cache holds
 *     was answered by the network during this page's life. That is what "confirmed" means,
 *     by construction: no flag to forget, no timestamp to compare.
 *  2. **The blob records its account**: whose queries it holds, taken from the last
 *     confirmed `me.session`. It is never put back into the cache as `me.session`; it lives
 *     here, and is used for two things: drawing the shell while the device is offline, and
 *     deciding whether the cache must be emptied when the network names someone else.
 *  3. **The blob holds no writes (MAP 225b).** Queued offline writes live in their own
 *     account-keyed store (pending-writes.ts). A blob saved by an earlier release may still hold
 *     some: `restoreClient` moves them out before anything else can happen to the blob
 *     (legacy-writes.ts) and never hands them to TanStack. If that move fails they are
 *     CARRIED in the blob (`legacyWrites`) and moved at the next start, never dropped.
 *  4. **A change is not done until the disk agrees.** The old account's rows must leave the
 *     disk before the new account is drawn; if the disk will not let them go, nothing is
 *     drawn (`status: 'failed'`).
 */

/** The IndexedDB key the query cache is saved under. */
export const QUERY_CACHE_KEY = 'servwell-query-cache';

export type SessionAnswer = inferOutput<TRPCOptionsProxy<AppRouter>['me']['session']>;

/**
 * The one place an account is built: a user in a shop with a role this app knows, which is
 * exactly what `SessionProvider` and the shells consume. Null when any of it is missing.
 */
function toAccount(f: {
  userId: string;
  email: string;
  name: string | null;
  orgId: string | null;
  role: string | null;
}): Session | null {
  if (!f.orgId || !f.role || !ROLES.includes(f.role as Role)) return null;
  return {
    userId: f.userId,
    email: f.email,
    name: f.name,
    orgId: f.orgId,
    role: f.role as Role,
  };
}

/**
 * The account a confirmed session can draw shop data for, or null: signed out, no shop yet,
 * or a member with no recognised role.
 */
export function accountFromSession(answer: SessionAnswer): Session | null {
  return answer.authenticated ? toAccount(answer) : null;
}

/** An account read back from disk, which is untrusted storage: anything malformed is none. */
export function parseAccount(raw: unknown): Session | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const a = raw as Record<string, unknown>;
  if (typeof a.userId !== 'string' || typeof a.email !== 'string') return null;
  if (a.name !== null && typeof a.name !== 'string') return null;
  if (typeof a.orgId !== 'string' || typeof a.role !== 'string') return null;
  return toAccount({
    userId: a.userId,
    email: a.email,
    name: a.name,
    orgId: a.orgId,
    role: a.role,
  });
}

/**
 * Whose data it is, as other tabs see it: the same person in the same shop. A role change is
 * not "someone else in another tab".
 */
export function accountKey(account: Session | null): string | null {
  return accountRefKey(account);
}

/**
 * Whose data it is, as THIS cache sees it: the person, the shop, and the role. What a role may
 * read is cached under keys every role shares (`report.revenue`'s by person, the reports
 * menu), so a demoted owner would otherwise keep drawing the owner's answers until each one
 * refetched, or for the life of the blob offline (MAP 225a S1).
 */
export function resetKey(account: Session | null): string | null {
  return account ? `${accountKey(account)}|${account.role}` : null;
}

export function isSessionQueryKey(queryKey: QueryKey): boolean {
  return pathOf(queryKey) === 'me.session';
}

/** Private pricing is online-only, including reads added by later pricing slices. */
function isPricingQueryKey(queryKey: QueryKey): boolean {
  const path = pathOf(queryKey);
  return path === 'pricing' || path?.startsWith('pricing.') === true;
}
function isPrivateQueryKey(queryKey: QueryKey): boolean {
  return isSessionQueryKey(queryKey) || isPricingQueryKey(queryKey);
}
/** Rule 1. Every other query keeps TanStack's default (only successful reads are saved). */
export function shouldDehydrateQuery(query: Query): boolean {
  return defaultShouldDehydrateQuery(query) && !isPrivateQueryKey(query.queryKey);
}

export type CacheAccountSnapshot = Readonly<{
  /** The account the cached queries belong to, or null for nobody. */
  account: Session | null;
  /**
   * `resetting` while a change of account is emptying the cache and the disk; `failed` when
   * the disk would not let the old rows go. The gate draws shop data only when `settled`.
   */
  status: 'settled' | 'resetting' | 'failed';
}>;

/**
 * What is stored: TanStack's own blob, plus whose it is, plus (only after a failed move)
 * an earlier release's queued writes, already in their row shape (MAP 225b).
 */
export type StoredClient = PersistedClient & {
  account?: unknown;
  legacyWrites?: readonly PendingWrite[];
};

/**
 * Moves an earlier release's queued writes out of a saved blob into their own store
 * (legacy-writes.ts). Resolves with what it could NOT move, to be carried in the blob until
 * the next start; `null` when nothing needs carrying. Never rejects.
 */
export type MoveLegacyWrites = (
  stored: StoredClient | undefined,
) => Promise<readonly PendingWrite[] | null>;

/**
 * A purge that failed, remembered past the app closing (Codex r3).
 *
 * `failed` lives in the tab's memory. With writes queued the old blob must be KEPT (it is
 * their only copy), so after the app closes the disk still holds the previous account's rows
 * under the previous account's label, and an offline start would draw them. This tiny
 * localStorage entry, a separate store with its own tiny write, says "don't". While it is
 * there, the saved account is never drawn: offline shows the blocking screen, and online
 * the first answer runs the purge again. It goes only once a purge (or the blob's removal)
 * has actually reached the disk.
 *
 * Its presence is what counts; `blocked` (the reset key that must not be drawn) and `at` are
 * there for whoever reads it in a device's storage.
 *
 * ⚠️ The accepted residual: IndexedDB refusing the purge AND localStorage refusing this
 * entry. Then this tab stays blocked in memory, and the next start has nothing to go on.
 */
export const PURGE_PENDING_KEY = 'servwell-cache-purge-pending';
export type MarkerStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export class CacheAccount {
  readonly persister: Persister;
  readonly #queryClient: QueryClient;
  readonly #kv: Pick<Kv, 'get' | 'set' | 'del'>;
  readonly #key: IDBValidKey;
  readonly #dehydrateOptions: DehydrateOptions;
  readonly #onAccountChange: (account: Session | null) => void;
  readonly #markers: MarkerStorage | undefined;
  #snapshot: CacheAccountSnapshot = { account: null, status: 'settled' };
  /** Bumped by every change of account, so an older purge cannot settle a newer one. */
  #generation = 0;
  #inflight: Promise<void> = Promise.resolve();
  /** While the old rows are removed: one write after, not one per removed query. */
  #quiet = false;
  /** Another tab changed account: this tab never writes the blob again. */
  #stopped = false;
  /**
   * An earlier release's queued writes that could not be moved to their own store this start
   * (MAP 225b). Every write of the blob carries them, and the blob is never removed while
   * they are here: they are its only copy.
   */
  #carry: readonly PendingWrite[] | null = null;
  // A pricing-only scrub must not use account purge: ordinary offline queries survive.
  #pricingScrub: StoredClient | null = null;
  #scrubWrite: Promise<void> | null = null;
  #afterPricingScrub: { next: Session | null } | undefined;
  readonly #listeners = new Set<() => void>();

  constructor(opts: {
    queryClient: QueryClient;
    kv: Pick<Kv, 'get' | 'set' | 'del'>;
    key?: IDBValidKey;
    dehydrateOptions: DehydrateOptions;
    /** Handed the raw saved blob before anything is restored (the draft migration). */
    beforeRestore?: (stored: unknown) => Promise<void>;
    /** Told once a change of account has reached the disk (other tabs are told from here). */
    onAccountChange?: (account: Session | null) => void;
    /** Where a failed purge is remembered across starts: localStorage in the app. */
    markerStorage?: MarkerStorage;
    /** Moves an earlier release's queued writes out of the blob (legacy-writes.ts). */
    moveLegacyWrites?: MoveLegacyWrites;
  }) {
    this.#markers = opts.markerStorage;
    this.#queryClient = opts.queryClient;
    this.#kv = opts.kv;
    this.#key = opts.key ?? QUERY_CACHE_KEY;
    this.#dehydrateOptions = opts.dehydrateOptions;
    this.#onAccountChange = opts.onAccountChange ?? (() => {});
    const kv = this.#kv;
    const key = this.#key;
    const beforeRestore = opts.beforeRestore;
    const moveLegacyWrites = opts.moveLegacyWrites;
    this.persister = {
      persistClient: async (client: PersistedClient) => {
        if (this.#quiet || this.#stopped || this.#pricingScrub) return;
        // ⚠️ The label is read HERE, in the same synchronous call in which TanStack just
        // dehydrated `client`. That pairing is the whole guarantee: rows and label from one
        // moment. Wrapping this persister in a throttle or a debounce (as
        // `createAsyncStoragePersister` does) would write an earlier moment's rows under a
        // later moment's label, which is the leak this file closes. A test pins it.
        const stored: StoredClient = this.#withCarry({
          ...client,
          clientState: {
            ...client.clientState,
            queries: client.clientState.queries.filter(
              (q) => !isPricingQueryKey(q.queryKey),
            ),
          },
          account: this.#snapshot.account,
        });
        await kv.set(key, stored);
      },
      restoreClient: async () => {
        const stored = (await kv.get(key)) as StoredClient | undefined;
        try {
          await beforeRestore?.(stored);
        } catch (err) {
          // A restore that throws makes TanStack delete the whole blob. A draft migration is
          // never worth that; it runs again at the next start.
          console.warn(
            '[servwell] moving old drafts failed; it runs again next time',
            err,
          );
        }
        // An earlier release's queued writes leave the blob HERE, before TanStack can check
        // its age or buster and remove it (persist.ts calls this first). What cannot be
        // moved now is carried in every later write of the blob.
        try {
          this.#carry = (await moveLegacyWrites?.(stored)) ?? null;
        } catch (err) {
          // The move never throws; if one ever does, every old write the blob holds is
          // carried, never dropped: this blob may be their only copy.
          this.#carry = carriedFrom(stored);
          console.warn('[servwell] moving old queued writes failed', err);
        }
        if (!stored) return undefined;
        const queries = Array.isArray(stored.clientState?.queries)
          ? stored.clientState.queries
          : [];
        if (queries.some((q) => isPricingQueryKey(q.queryKey))) {
          // Preserve labels, time/buster, ordinary reads and raw/carry legacy work.
          this.#pricingScrub = this.#withCarry({
            ...stored,
            clientState: {
              ...stored.clientState,
              queries: queries.filter((q) => !isPrivateQueryKey(q.queryKey)),
            },
          });
          await this.#retryPricingScrub();
        } else {
          // A preexisting account-purge marker remains authoritative after a scrub too.
          this.#set(
            this.#purgePending()
              ? { account: null, status: 'failed' }
              : { account: parseAccount(stored.account), status: 'settled' },
          );
        }
        return {
          timestamp: stored.timestamp,
          buster: stored.buster,
          clientState: {
            ...stored.clientState,
            queries: queries.filter((q) => !isPrivateQueryKey(q.queryKey)),
            // Never a write: TanStack must not run an earlier release's writes (MAP 225b).
            mutations: [],
          },
        };
      },
      removeClient: async () => {
        // Expired, busted, or signed out: nothing cached has an account any more.
        this.#generation += 1;
        this.#set({ account: null, status: 'settled' });
        await this.#removeBlob();
        // The stale blob is gone, so a pending purge has nothing left to protect.
        this.#clearPurgePending();
      },
    };
  }

  /** The carried writes, on every blob this persister writes. */
  #withCarry(stored: StoredClient): StoredClient {
    return this.#carry?.length ? { ...stored, legacyWrites: this.#carry } : stored;
  }

  /**
   * Remove the blob, or, while old writes are carried in it, leave a blob holding only them:
   * no queries, no account. They are moved at the next start.
   */
  async #removeBlob(): Promise<void> {
    if (this.#carry?.length) {
      await this.#kv.set(this.#key, this.#carryOnly());
      return;
    }
    await this.#kv.del(this.#key);
  }

  #carryOnly(): StoredClient {
    return this.#withCarry({
      timestamp: Date.now(),
      buster: '',
      clientState: { queries: [], mutations: [] },
      account: null,
    });
  }

  snapshot = (): CacheAccountSnapshot => this.#snapshot;

  account(): Session | null {
    return this.#snapshot.account;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  #set(next: CacheAccountSnapshot): void {
    this.#snapshot = next;
    for (const listener of this.#listeners) listener();
  }

  /** Write the cache to disk now, the way the provider's own subscription does. */
  persist(): Promise<void> {
    return persistQueryClientSave({
      queryClient: this.#queryClient,
      persister: this.persister,
      dehydrateOptions: this.#dehydrateOptions,
    });
  }

  /** Every query but `me.session` (a network answer, which stays one), with no write each. */
  #removeAccountQueries(): void {
    this.#quiet = true;
    try {
      this.#queryClient.removeQueries({
        predicate: (q) => !isSessionQueryKey(q.queryKey),
      });
    } finally {
      this.#quiet = false;
    }
  }

  /**
   * The network answered `me.session`, and `next` is the account it named (null: nobody who
   * can see shop data).
   *
   * - The same account in the same role: nothing to empty. A changed name or email only
   *   refreshes the label on disk, so an offline start draws the shell as it is now.
   * - Anyone else, any other role, or nobody: empty the QUERIES (never the mutations),
   *   write the emptied cache to disk under the new label, and only then report settled.
   *
   * ⚠️ The order is load-bearing. The old rows go FIRST, while the label still names them,
   * and without a write per removal; only then does the label change, and then ONE write.
   * No write can ever pair the new account with the old account's rows.
   */
  confirm(next: Session | null): Promise<void> {
    if (this.#stopped) return Promise.resolve();
    if (this.#pricingScrub) {
      this.#afterPricingScrub = { next };
      return this.#retryPricingScrub();
    }
    const { account: current, status } = this.#snapshot;
    const same = resetKey(current) === resetKey(next);
    // A change to this same account is still purging: it will settle itself.
    if (same && status === 'resetting') return this.#inflight;
    if (same && status === 'settled') {
      if (next === null || sameLabel(current, next)) return Promise.resolve();
      this.#set({ account: next, status: 'settled' });
      return this.persist().catch(() => {});
    }
    // Anyone else, or a purge that failed (here or in an earlier session): purge, towards
    // the account the network has just named.
    this.#set({ account: current, status: 'resetting' });
    this.#removeAccountQueries();
    return this.#purge(next, resetKey(current));
  }

  /** Try the purge again after it failed (the blocking screen's "Try again"). */
  retry(): Promise<void> {
    if (this.#pricingScrub) return this.#retryPricingScrub();
    if (this.#snapshot.status !== 'failed') return this.#inflight;
    this.#removeAccountQueries();
    return this.#purge(this.#snapshot.account, null);
  }

  /** Retry the exact sanitized blob, not a query purge or a newly labelled snapshot. */
  #retryPricingScrub(): Promise<void> {
    if (this.#stopped || !this.#pricingScrub) return Promise.resolve();
    if (this.#scrubWrite) return this.#scrubWrite;
    const saved = this.#pricingScrub,
      generation = this.#generation;
    const account = this.#purgePending() ? null : parseAccount(saved.account);
    this.#set({ account, status: 'resetting' });
    this.#scrubWrite = (async () => {
      try {
        await this.#kv.set(this.#key, saved);
        if (this.#stopped || generation !== this.#generation) return;
        this.#pricingScrub = null;
        this.#set(
          this.#purgePending()
            ? { account: null, status: 'failed' }
            : { account, status: 'settled' },
        );
        const deferred = this.#afterPricingScrub;
        this.#afterPricingScrub = undefined;
        if (deferred) await this.confirm(deferred.next);
      } catch {
        // Return filtered reads, never throw into TanStack's delete-on-restore-error path.
        if (!this.#stopped && generation === this.#generation)
          this.#set({ account, status: 'failed' });
      }
    })().finally(() => {
      this.#scrubWrite = null;
    });
    return this.#scrubWrite;
  }

  #purgePending(): boolean {
    try {
      return this.#markers?.getItem(PURGE_PENDING_KEY) != null;
    } catch {
      return false;
    }
  }

  #markPurgePending(blocked: string | null): void {
    try {
      if (!this.#markers) throw new Error('no localStorage');
      this.#markers.setItem(
        PURGE_PENDING_KEY,
        JSON.stringify({ blocked: blocked ?? 'blocked', at: new Date().toISOString() }),
      );
    } catch (err) {
      console.warn(
        "[servwell] couldn't record that the previous account's data is still on this device; this tab stays blocked",
        err,
      );
    }
  }

  #clearPurgePending(): void {
    try {
      this.#markers?.removeItem(PURGE_PENDING_KEY);
    } catch {
      // Left behind, it only blocks the next offline start until a purge runs again.
    }
  }

  /**
   * Get the old rows off the disk, or refuse to settle (MAP 225a S2). In order, until one
   * works: write the emptied cache; write it again; write a blob with no account and no
   * queries (carried old writes kept); remove the blob, but ONLY when it carries no old
   * writes, since it is their only copy. Queued writes made in this release are
   * in their own store and never in the blob (MAP 225b). If none works, `failed`: the
   * previous account's rows may still be on this device, and the gate draws nothing over
   * them.
   */
  #purge(next: Session | null, blocked: string | null): Promise<void> {
    const generation = ++this.#generation;
    this.#set({ account: next, status: 'resetting' });
    const steps = [
      () => this.persist(),
      () => this.persist(),
      () => this.#saveWithNoAccount(),
      () => this.#removeIfNothingQueued(),
    ];
    this.#inflight = (async () => {
      for (const step of steps) {
        try {
          await step();
        } catch {
          continue;
        }
        if (generation !== this.#generation) return;
        // Only now, with the old rows off the disk, may the reminder go.
        this.#clearPurgePending();
        this.#set({ account: next, status: 'settled' });
        this.#onAccountChange(next);
        return;
      }
      if (generation !== this.#generation) return;
      this.#markPurgePending(blocked);
      this.#set({ account: next, status: 'failed' });
    })();
    return this.#inflight;
  }

  /** The writes the blob holds: only carried old ones (MAP 225b). */
  #queuedWrites(): readonly PendingWrite[] {
    return this.#carry ?? [];
  }

  async #saveWithNoAccount(): Promise<void> {
    await this.#kv.set(this.#key, this.#carryOnly());
  }

  async #removeIfNothingQueued(): Promise<void> {
    if (this.#queuedWrites().length > 0) {
      throw new Error('old writes are carried: the blob is their only copy on disk');
    }
    await this.#kv.del(this.#key);
  }

  /**
   * Signing out (MAP 225b): this tab never writes the blob again, then the blob goes (or,
   * while it carries old writes, a blob holding only them stays). The cache is NOT cleared:
   * clearing it dropped queued writes, and cost a full save per removed query. The page
   * reloads next, which empties memory.
   *
   * If the disk will not let the blob go, the purge reminder is left, so an offline start
   * never draws the account that just signed out; the next online start purges it.
   */
  async removeSaved(): Promise<void> {
    this.#stopped = true;
    this.#generation += 1;
    const blocked = resetKey(this.#snapshot.account);
    this.#set({ account: null, status: 'settled' });
    try {
      await this.#removeBlob();
    } catch (err) {
      this.#markPurgePending(blocked);
      throw err;
    }
    this.#clearPurgePending();
  }

  /**
   * Another tab changed who is signed in. This tab draws nothing more, and never writes the
   * blob again: its saves would put its old state over the blob the other tab just wrote
   * (MAP 225a S5). Its queries go too; queued writes are in their own store, untouched.
   */
  stop(): void {
    this.#stopped = true;
    this.#generation += 1;
    this.#removeAccountQueries();
    this.#set({ account: null, status: 'settled' });
  }
}

function sameLabel(a: Session | null, b: Session): boolean {
  return (
    a !== null && resetKey(a) === resetKey(b) && a.email === b.email && a.name === b.name
  );
}
